import { EmptyState } from '@breward/ui';
import { withSentryReactRouterV6Routing } from '@sentry/react';

import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from './layouts/AppLayout/AppLayout';
import ContestLayout from './layouts/ContestLayout/ContestLayout';
import EntryLayout from './layouts/EntryLayout/EntryLayout';

// Lazy load UI pages
const ShowStylePage = lazy(() => import('@breward/ui').then(m => ({ default: m.ShowStylePage })));
const ScoresheetPage = lazy(() => import('@breward/ui').then(m => ({ default: m.ScoresheetPage })));

// Lazy load app pages
const BreweryPage = lazy(() => import('./pages/BreweryPage/BreweryPage'));
const ContestPage = lazy(() => import('./pages/ContestPage/Contest.page'));
const ContestsPage = lazy(() => import('./pages/ContestsPage/Contests.page'));
const DashboardPage = lazy(() => import('./pages/DashboardPage/Dashboard.page'));
const EntriesPage = lazy(() => import('./pages/EntriesPage/Entries.page'));
const OrdersPage = lazy(() => import('./pages/OrdersPage/Orders.page'));
const EntryPage = lazy(() => import('./pages/EntryPage/Entry.page'));
const OrderPage = lazy(() => import('./pages/OrderPage/Order.page'));
const LoginPage = lazy(() => import('./pages/Login/Login.page'));
const PayForOrderPage = lazy(() => import('./pages/PayForOrderPage/PayForOrder.page'));
const PaymentStatusPage = lazy(() => import('./pages/PaymentStatusPage/PaymentStatusPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));
const RegisterEntryPage = lazy(() => import('./pages/RegisterEntryPage/RegisterEntry.page'));
const StripePaymentPage = lazy(() => import('./pages/StripePaymentPage/StripePaymentPage'));
const ContestsEditionResultsPage = lazy(() => import('./pages/ContestsEditionResultsPage/ContestsEditionResults.page'));

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const PageLoader = () => (
  <div className="flex h-full w-full flex-1 items-center justify-center p-8">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
  </div>
);

const Router = () => {
  const [t] = useTranslation();
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <SentryRoutes key={location.pathname} location={location}>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/reset-password"
          element={<LoginPage defaultForm={'PASSWORD_RESET'} />}
        />

        <Route path="/app/" element={<AppLayout />}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="contests" element={<ContestsPage />} />
          <Route path="entries" element={<EntriesPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route
            path="messages"
            element={
              <EmptyState
                message={t(
                  'global:shared.toBeImplemented',
                  'To be implemented...',
                )}
              />
            }
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="breweries/:id" element={<BreweryPage />} />
          <Route path="entry/:entryId" element={<EntryPage />} />
          <Route path="scoresheet/:scoresheetId" element={<div className='mt-6'><ScoresheetPage /></div>} />
          <Route path="orders/:orderId" element={<OrderPage />} />
        </Route>

        <Route path="/app/contest/" element={<ContestLayout />}>
          <Route path=":id" element={<ContestPage />} />
          <Route path=":id/:contestsEditionId/results" element={<ContestsEditionResultsPage />} />
          <Route
            path=":contestsEditionId/register-entry"
            element={<RegisterEntryPage />}
          />
        </Route>

        <Route path="/app/orders/:orderId" element={<EntryLayout />}>
          <Route path="checkout" element={<PayForOrderPage />} />
        </Route>

        <Route path="/app/styles/">
          <Route path=":id" element={<ShowStylePage />} />
        </Route>

        <Route path="/app/payments/" element={<EntryLayout />}>
          <Route path=":id" element={<PaymentStatusPage />} />
          <Route path=":id/stripe" element={<StripePaymentPage />} />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
};

export default Router;
